import type { PartIndexabilityDecision } from "./part-indexability.js";
import { CANONICAL_SITE_URL, normalizeIdentityValue } from "./part-indexability.js";
import { serializeJsonForHtml } from './html-safe-json.js';
import {
  buildSupplierRedirectPath,
  outboundCapabilityIsFresh,
  outboundSourceKey,
} from "./supplier-redirect.js";

export type OfferFreshness = "fresh" | "cached" | "expired";
export type AlternativeEvidenceTier = "manufacturer_confirmed" | "curated" | "algorithmic";

export interface PartLandingOffer {
  id: string;
  stableKey: string;
  sourceId: string;
  sourceItemKey: string;
  retailer: string;
  sourceType: string;
  sellerName: string | null;
  sellerVerified: boolean | null;
  partNumber: string;
  manufacturer: string;
  condition: string;
  price: number | null;
  currency: string;
  availability: number | null;
  availabilityText: string;
  stockConfidence: "high" | "medium" | "low";
  observedAt: string;
  freshness: OfferFreshness;
  productUrl: string;
  imageUrl: string | null;
  vatStatus: string;
  shippingStatus: string;
  importStatus: string;
  deliveryEstimate: string;
  warranty: string;
  returns: string;
  costComplete: boolean;
  isFirstParty: boolean;
  purchasable: boolean;
  /** Interaction-only fields returned by a live refresh; never populated in cached SSR data. */
  outboundOfferReference?: string | null;
  outboundCapability?: string | null;
  outboundCapabilityExpiresAt?: string | null;
}

export interface PartLandingAlternative {
  id: string;
  partNumber: string;
  manufacturer: string;
  relationship: "successor" | "substitute" | "possible_variant";
  evidenceTier: AlternativeEvidenceTier;
  evidenceLabel: string;
  evidenceUrl: string | null;
  confidence: number;
  requiresChanges: boolean;
  fitNotes: string | null;
  canonicalUrl: string | null;
}

export interface PartLandingSpecification {
  name: string;
  value: string;
}

export interface PartLandingEvidenceLink {
  label: string;
  url: string;
  type: "datasheet" | "technical" | "manufacturer";
}

export interface PartLandingViewModel {
  version: 1;
  partId: string;
  slug: string;
  canonicalUrl: string;
  manufacturer: string;
  formattedMpn: string;
  normalizedManufacturer: string;
  normalizedMpn: string;
  acceptedNormalizedManufacturers: string[];
  model: string | null;
  family: string | null;
  category: string | null;
  categoryLabel: string | null;
  description: string;
  imageUrls: string[];
  lifecycle: {
    status: string;
    source: string | null;
    evidenceUrl: string | null;
    leadTimeDays: number | null;
    lastTimeBuy: string | null;
  } | null;
  offers: PartLandingOffer[];
  alternatives: PartLandingAlternative[];
  specifications: PartLandingSpecification[];
  evidenceLinks: PartLandingEvidenceLink[];
  researchSummary: string | null;
  lastMaterialUpdate: string;
  eligibility: PartIndexabilityDecision;
  metadata: {
    title: string;
    description: string;
    robots: string;
  };
  internalLinks: Array<{ label: string; url: string; kind: "manufacturer" | "family" | "category" | "alternative" }>;
}

export function buildPartLandingSupplierRedirect(input: {
  offer: PartLandingOffer;
  searchId: string | null | undefined;
  searchProof: string | null | undefined;
  partSlug: string;
  normalizedMpn: string;
  rankPosition: number;
  resultCount: number;
  now?: number;
}): string | null {
  const { offer } = input;
  if (
    offer.isFirstParty
    || !input.searchId
    || !input.searchProof
    || !offer.outboundCapability
    || !offer.outboundOfferReference
    || !outboundCapabilityIsFresh(offer.outboundCapabilityExpiresAt, input.now)
  ) return null;

  return buildSupplierRedirectPath({
    destination: offer.productUrl,
    outboundCapability: offer.outboundCapability,
    searchId: input.searchId,
    searchProof: input.searchProof,
    sourceKey: outboundSourceKey(offer.sourceId),
    offerId: offer.outboundOfferReference,
    surface: "part_landing",
    partSlug: input.partSlug,
    normalizedMpn: input.normalizedMpn,
    rankPosition: input.rankPosition,
    resultCount: input.resultCount,
    matchType: "exact",
    scanConfidence: offer.stockConfidence === "low" ? "review" : offer.stockConfidence,
    costIsComplete: offer.costComplete,
  });
}

function priceText(offer: PartLandingOffer | undefined): string | null {
  if (!offer || offer.price == null || !Number.isFinite(offer.price)) return null;
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: offer.currency || "GBP",
      maximumFractionDigits: 2,
    }).format(offer.price);
  } catch {
    return `${offer.currency || "GBP"} ${offer.price.toFixed(2)}`;
  }
}

export function buildPartLandingMetadata(input: {
  manufacturer: string;
  formattedMpn: string;
  offers: PartLandingOffer[];
  lifecycleStatus?: string | null;
  indexable: boolean;
}): PartLandingViewModel["metadata"] {
  const title = `${input.manufacturer} ${input.formattedMpn} Price, Stock & Alternatives | Control Exchange`;
  const priced = input.offers.find((offer) => offer.price != null);
  const price = priceText(priced);
  const stocked = input.offers.filter((offer) => (offer.availability ?? 0) > 0).length;
  const signals = [
    price ? `Prices from ${price}.` : null,
    input.offers.length > 0 ? `${input.offers.length} exact offer${input.offers.length === 1 ? "" : "s"} checked.` : "Check current availability.",
    stocked > 0 ? `${stocked} source${stocked === 1 ? "" : "s"} reporting stock.` : null,
    input.lifecycleStatus ? `Lifecycle: ${input.lifecycleStatus}.` : null,
  ].filter(Boolean).join(" ");
  const description = `Compare ${input.manufacturer} ${input.formattedMpn} price, stock, condition, delivery caveats and evidence-ranked alternatives for UK and European procurement. ${signals}`.slice(0, 300);
  return {
    title,
    description,
    robots: input.indexable
      ? "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      : "noindex, follow",
  };
}

export function buildPartStructuredData(model: PartLandingViewModel): Record<string, unknown> {
  const breadcrumbs = {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: CANONICAL_SITE_URL },
      { "@type": "ListItem", position: 2, name: model.manufacturer, item: `${CANONICAL_SITE_URL}/manufacturers/${slugify(model.manufacturer)}` },
      { "@type": "ListItem", position: 3, name: model.formattedMpn, item: model.canonicalUrl },
    ],
  };
  const graph: Array<Record<string, unknown>> = [
    {
      "@type": "WebPage",
      "@id": model.canonicalUrl,
      url: model.canonicalUrl,
      name: model.metadata.title,
      description: model.metadata.description,
      dateModified: model.lastMaterialUpdate,
      isPartOf: { "@type": "WebSite", url: CANONICAL_SITE_URL, name: "Control Exchange" },
    },
    breadcrumbs,
  ];

  // Google merchant Offer markup is deliberately limited to inventory bought
  // from Control Exchange. Distributor comparisons stay visible HTML only.
  const firstPartyOffers = model.offers.filter((offer) => (
    offer.isFirstParty && offer.purchasable && offer.price != null
  ));
  if (firstPartyOffers.length > 0) {
    graph.push({
      "@type": "Product",
      "@id": `${model.canonicalUrl}#product`,
      name: `${model.manufacturer} ${model.formattedMpn}`,
      description: model.description,
      mpn: model.formattedMpn,
      model: model.model || model.formattedMpn,
      brand: { "@type": "Brand", name: model.manufacturer },
      image: model.imageUrls.length > 0 ? model.imageUrls : undefined,
      offers: firstPartyOffers.map((offer) => ({
        "@type": "Offer",
        url: offer.productUrl,
        price: Number(offer.price).toFixed(2),
        priceCurrency: offer.currency,
        availability: (offer.availability ?? 0) > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        itemCondition: schemaCondition(offer.condition),
        seller: { "@type": "Organization", name: offer.sellerName || "Control Exchange Seller" },
      })),
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

function schemaCondition(condition: string): string {
  const normalized = condition.toLowerCase();
  if (normalized.includes("new") || normalized.includes("sealed")) return "https://schema.org/NewCondition";
  if (normalized.includes("refurb")) return "https://schema.org/RefurbishedCondition";
  return "https://schema.org/UsedCondition";
}

export function slugify(value: unknown): string {
  return String(value || "")
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function safePublicUrl(value: unknown): string | null {
  try {
    const raw = String(value || "").trim();
    if (!raw || raw.length > 4_000) return null;
    const parsed = new URL(raw, CANONICAL_SITE_URL);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export { serializeJsonForHtml } from './html-safe-json.js';

export function serializePartLandingModel(model: PartLandingViewModel): string {
  return serializeJsonForHtml(model);
}

export function parsePartLandingBootstrap(value: string | null | undefined): PartLandingViewModel | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as PartLandingViewModel;
    if (parsed?.version !== 1 || !parsed.slug || !parsed.canonicalUrl) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function liveOfferMatchesModel(
  offer: { normalizedMpn?: unknown; manufacturer?: unknown },
  model: PartLandingViewModel,
): boolean {
  const mpnMatches = normalizeIdentityValue(offer.normalizedMpn) === model.normalizedMpn;
  const manufacturer = normalizeIdentityValue(offer.manufacturer);
  return mpnMatches && Boolean(manufacturer) && model.acceptedNormalizedManufacturers.includes(manufacturer);
}
